import {ChangeDetectorRef, Component, HostListener, NgZone} from '@angular/core';
import {DarkModeToggleComponent} from "../../common/dark-mode-toggle/dark-mode-toggle.component";
import {NgClass, NgIf, NgStyle} from "@angular/common";
import {ActivatedRoute, NavigationEnd, Router, RouterOutlet} from "@angular/router";
import {SidebarComponent} from "../../common/sidebar/sidebar.component";
import {SubscriptionBannerComponent} from "../../common/subscription-banner/subscription-banner.component";
import {filter, Subject} from "rxjs";
import {selectIsSidebarCollapsed} from "../../state/ui/ui.selectors";
import {Store} from "@ngrx/store";
import {AppState} from "../../state/reducers";
import {HeaderService} from "../../services/header.service";
import {SidebarService} from "../../services/sidebar.service";
import * as UiActions from "../../state/ui/ui.actions";
import {takeUntil} from "rxjs/operators";

@Component({
  selector: 'app-settings-layout',
  standalone: true,
  imports: [
    DarkModeToggleComponent,
    NgIf,
    RouterOutlet,
    SidebarComponent,
    SubscriptionBannerComponent,
    NgClass,
    NgStyle
  ],
  templateUrl: './settings-layout.component.html',
  styleUrl: './settings-layout.component.scss'
})
export class SettingsLayoutComponent {

  private destroy$ = new Subject<void>();

  sidebarCollapsed$ = this.store.select(selectIsSidebarCollapsed);
  sidebarCollapsed = false;
  isDesktop = window.innerWidth >= 1024;
  showMobileSidebar = false;

  // Header properties
  pageTitle: string = '';
  pageDescription: string = '';
  showHeader: boolean = true;

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private headerService: HeaderService,
    private sidebarService: SidebarService,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // Check if desktop/mobile on init
    this.checkIfDesktop();

    // Load saved state from localStorage if needed
    const savedState = localStorage.getItem('sidebarCollapsed');
    if (savedState) {
      this.store.dispatch(UiActions.toggleSidebarCollapsed({
        isCollapsed: savedState === 'true'
      }));
    }

    // Subscribe to the store to keep local state in sync
    this.sidebarCollapsed$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(isCollapsed => {
      this.sidebarCollapsed = isCollapsed;
    });

    // Listen to route changes to update title automatically
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.updateTitleFromRoute();
    });

    // Subscribe to sidebar toggle events
    this.sidebarService.sidebarToggle
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.toggleMobileSidebar();
      });
  }

  ngAfterViewInit(): void {
    // Subscribe to the header service after the view is initialized
    // This prevents the ExpressionChangedAfterItHasBeenCheckedError
    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          // Subscribe to the header service
          this.headerService.headerState$.pipe(
            takeUntil(this.destroy$)
          ).subscribe(state => {
            this.pageTitle = state.title;
            this.pageDescription = state.description;
            this.showHeader = state.visible;
            this.cdr.detectChanges();
          });

          // Set default title based on current route
          this.updateTitleFromRoute();
        });
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:resize')
  onResize(): void {
    const wasDesktop = this.isDesktop;
    this.checkIfDesktop();

    // If transitioning from desktop to mobile, hide the sidebar
    if (wasDesktop !== this.isDesktop && !this.isDesktop) {
      this.showMobileSidebar = false;
    }
  }

  checkIfDesktop(): void {
    this.isDesktop = window.innerWidth >= 1024;
  }

  onSidebarCollapsedChange(collapsed: boolean): void {
    this.store.dispatch(UiActions.toggleSidebarCollapsed({ isCollapsed: collapsed }));
    // Save to localStorage
    localStorage.setItem('sidebarCollapsed', String(collapsed));
  }

  toggleMobileSidebar(): void {
    this.showMobileSidebar = !this.showMobileSidebar;

    // Optional: add body scroll lock when sidebar is open on mobile
    if (this.showMobileSidebar) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }

  // Helper method to extract title from route data
  private updateTitleFromRoute(): void {
    let title = '';
    let currentRoute = this.activatedRoute.root;

    // Navigate to the leaf route
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    // Extract title from the route data if available
    if (currentRoute.snapshot.data['title']) {
      title = currentRoute.snapshot.data['title'];
      this.headerService.setTitle(title);
    }
  }

}
